import { useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import ModalService from "src/modals"
import {
  selectAllies,
  selectCurrentTeam,
  selectIsCurrentPlayerReady,
  selectPlayerFood,
  selectResearchPoints,
} from "src/store/game"

import Modal from "src/components/modals/Modal"

import "./TradeModal.css"

// Food and research points are the only tradeable things: both are per-player pools, so a transfer
// is two field updates. Units and cells are not, and the backend has no notion of trading them.
const TradeModal = () => {
  const { t } = useTranslation()
  const allies = useSelector(selectAllies)
  const food = useSelector(selectPlayerFood)
  const researchPoints = useSelector(selectResearchPoints)
  const isReady = useSelector(selectIsCurrentPlayerReady)
  const team = useSelector(selectCurrentTeam)

  const [allyId, setAllyId] = useState(allies[0]?.user.id ?? null)
  const [foodAmount, setFoodAmount] = useState(0)
  const [pointsAmount, setPointsAmount] = useState(0)

  const foodLabel = team === "human" ? t("trade.food") : t("trade.meat")

  // Clamp here as well as on the server: typing 900 when you hold 200 should not need a round trip
  // to find out, and the backend's InsufficientResources is a rule, not a form validator.
  const clamp = (value, ceiling) => Math.max(0, Math.min(Number(value) || 0, ceiling))

  const send = () => {
    if (!allyId) return
    if (foodAmount <= 0 && pointsAmount <= 0) return
    const sent = wsClient.send("game", {
      action: "trade",
      ally_id: allyId,
      food: foodAmount,
      research_points: pointsAmount,
    })
    // Only close on a send that actually left. On a dropped socket the toast says it did not, and
    // closing anyway would look exactly like a trade that went through.
    if (sent) ModalService.hide()
  }

  return (
    <Modal width="min(560px, 80%)" isolated={false} scope="map" top="14vh" maxHeight="66vh">
      <div className="trade-header p-small">
        <span>{t("trade.sendToAnAlly")}</span>
        <span className="trade-cost p-mini">{t("trade.costsOneAction")}</span>
      </div>

      {allies.length === 0 ? (
        // Reachable only if a teammate is eliminated while the modal is open — the button that
        // opens it is already hidden without allies.
        <p className="p-mini trade-empty">{t("trade.noAllies")}</p>
      ) : (
        <>
          <div className="trade-row">
            <span className="p-small">{t("trade.ally")}</span>
            <select className="trade-select" value={allyId ?? ""} onChange={(e) => setAllyId(Number(e.target.value))}>
              {allies.map((ally) => (
                <option key={ally.user.id} value={ally.user.id}>
                  {ally.user.username}
                </option>
              ))}
            </select>
          </div>

          <div className="trade-row">
            <span className="p-small">
              {foodLabel} <span className="trade-held p-mini">{t("trade.held", { amount: food })}</span>
            </span>
            <input
              className="trade-input"
              type="number"
              min="0"
              max={food}
              value={foodAmount}
              onChange={(e) => setFoodAmount(clamp(e.target.value, food))}
            />
          </div>

          <div className="trade-row">
            <span className="p-small">
              {t("trade.research")} <span className="trade-held p-mini">{t("trade.held", { amount: researchPoints })}</span>
            </span>
            <input
              className="trade-input"
              type="number"
              min="0"
              max={researchPoints}
              value={pointsAmount}
              onChange={(e) => setPointsAmount(clamp(e.target.value, researchPoints))}
            />
          </div>

          <button
            className="trade-send"
            onClick={send}
            disabled={isReady || !allyId || (foodAmount <= 0 && pointsAmount <= 0)}
          >
            {isReady ? t("trade.lockedIn") : t("trade.send")}
          </button>
        </>
      )}
    </Modal>
  )
}

export default TradeModal
